import { Permissions } from '../../../../models/enums/permissions';
import { withStyles } from '@material-ui/core';
import * as React from 'react';
import { connect } from "react-redux";
import { compose } from 'redux';
import { DataGroup } from '../../../../components/data-group/DataGroup';
import { DataGroupPoint } from '../../../../components/data-group/DataGroupPoint';
import { ConfirmationDialogTrigger } from '../../../../components/dialog-types/ConfirmationDialogTrigger';
import { PageActions, TextInput } from '../../../../components/form-elements/_index';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { subscribeToRouteChange } from '../../../../hocs/PageWrapper';
import { ServiceCenter } from '../../../../models/utilities/service-center';
import { getServiceCenterData } from '../../../../selectors/utility-selectors';
import { UsersSvc } from '../../../../shared/services/users-service';
import { IApplicationState } from "../../../../store/IApplicationState";
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { toLongDateTime } from '../../../../utils/date-utils';
import { navigateToPage } from '../../../../utils/history';
import { Mapper } from '../../../../utils/mapper';
import { ModelValidator } from '../../../../utils/model-validator';
import * as actions from '../../../../support/actions';
import { PermissionMatrix, UserPermissionsByServiceCenter } from '../components/PermissionMatrix';
import { UserCreateCallout } from '../components/UserCreateCallout';
import { UserCreate } from '../../../../support/models/users/user-create';
import { UserRead } from '../../../../support/models/users/user-read';
import { UserUpdate } from '../../../../support/models/users/user-update';
import "../../../../support/styles.scss";

// #region Component State and Props

const defaultState = {
    user : new UserRead(),
    permissions: {} as UserPermissionsByServiceCenter,
    errorMsgs : {} as any
};

type State = Readonly<typeof defaultState>;

interface IProps extends IBaseSceneProps {
    clearUserSearch : any;
    serviceCenters  : ServiceCenter[];
}

// #endregion

class UserDetailsSceneComponent extends React.Component<IProps, State> {

    public userSvc : UsersSvc = new UsersSvc();
    public state: State = defaultState;

    public async componentDidMount() {

        const userId = AppUtils.stringParam(this.props.match, 'id');
        const user = await this.userSvc.getUser(userId);

        if (user.status === StatusCode.Success) {
            this.setState({
                user: user.data,
                permissions : user.data.permissions || {},
                errorMsgs: {}
            });
        }
        else {
            this.setState({
                user: this.state.user
            });
        }
    }

    public onRouteChange(route: any) {        
        if (route.pathname.indexOf(Routes.SUPPORT_USERS) === -1) {
            this.props.clearUserSearch();
        }
    }

    public render() {

        const { user } = this.state;

        return user ?
            <PageContent pageName={PageNames.SUPPORT_USER_DETAILS}>
                <PageHeading
                    shouldStick={ true }
                    title={ this.getTitle() }>
                    <PageActions
                        id="edit-user-form"
                        onEdit={ this.onSave }
                        editLabel="Save"
                        editPermissions={[
                            Permissions.SupportUsersCreate,
                            Permissions.SupportUsersSave
                        ]} 
                        onDelete={ this.onReset }
                        deleteLabel="Cancel"
                        deletePermissions={[
                            Permissions.SupportUsersCreate,
                            Permissions.SupportUsersSave
                        ]} 
                    />
                </PageHeading>
                <PageBody>

                    {
                        this.state.user.id 
                        ? <DataGroup title="Basic Information">
                                <DataGroupPoint
                                    label="Display Name"
                                    value={user.displayName} />
                                <DataGroupPoint
                                    label="First Name"
                                    value={user.firstName} />
                                <DataGroupPoint
                                    label="Last Name"
                                    value={user.lastName} />
                                <DataGroupPoint
                                    label="Email Address"
                                    value={user.email} />
                                <DataGroupPoint
                                    label="Last Modified"
                                    value={ toLongDateTime(user.lastModified) } />
                            </DataGroup> : 
                            <div className="row">
                                <div className="col-md-6">
                                    <TextInput
                                        type="text"
                                        id="emailAddress"
                                        error={this.state.errorMsgs.email}
                                        label="Email Address"
                                        onChange={ this.onEmailChange }
                                        value={ this.state.user.email } />
                                </div>
                            </div>
                    }
                     
                    <UserCreateCallout />

                    <PermissionMatrix
                        serviceCenters={ this.props.serviceCenters }
                        permissions={ this.state.permissions }
                        onChange={ this.onPermissionChange }
                    />

                    {
                        this.state.user.id ?
                        <HasPermission requiredPermissions={[
                            Permissions.SupportUsersDelete
                        ]}>
                            <ConfirmationDialogTrigger
                                title="Delete this User?"
                                body="Are you sure you want to delete this user?  This action can not be undone"
                                successCallback={ this.onDelete }
                                triggerText="Delete User"                            
                                triggerIcon="la-trash"
                                triggerClass="delete-shipment-button" /> 
                        </HasPermission> : null
                    }
                </PageBody>
            </PageContent> : null;
    }

    /**
     * Handler for deleting the current user
     */
    private onDelete = () => {
        if (this.state.user.id) {
            this.userSvc.deleteUser(this.state.user.id);
        }
    }

    private onEmailChange = (e: any) => {
        this.setState({
            user: {
                ...this.state.user,
                email: e.target.value
            }
        });
    }

    private onPermissionChange = (permissions: UserPermissionsByServiceCenter) => {
        this.setState({
            permissions
        });
    }

    private onSave = (e: any) => {
        e.preventDefault();

        const tmpUser = !this.state.user.id 
            ? Mapper.deserialize(UserCreate, { ...this.state.user, permissions: this.state.permissions })
            : Mapper.deserialize(UserUpdate, { ...this.state.user, permissions: this.state.permissions });
        
        const msgs = ModelValidator.validate(tmpUser);
        if (AppUtils.IsEmpty(msgs)) {
            this.userSvc.saveUser(this.state.user, this.state.permissions);
        }
        else {
            this.setState({
                errorMsgs: msgs
            });
        }
                
    }

    private onReset = () => {
        navigateToPage('/support/users');
    }

    private getTitle = () => {
        if (this.state.user.id) {
            return this.state.user.displayName ? this.state.user.displayName : this.state.user.email;
        }
        else {
            return 'Create New User';
        }
    }

}

const mapStateToProps = (state: IApplicationState) => {
    return {
        serviceCenters: getServiceCenterData(state)
    };
};

const mapDispatchToProps = (reduxDispatch: any) => {
    return {
        clearUserSearch: () => reduxDispatch(actions.clearUserSearch())
    };
};

const styles = () => ({
    root: {
        width: '100%'
    },
    heading: {
        fontSize: '2rem',
        backgroundColor: '#f7f9fb',
        borderBottom: '1px solid #333',
        borderLeft: '3px solid #39B6E9'
    }
});

const UserDetailsScene = compose(
    withStyles(styles, {
        name: 'UserDetailsSceneComponent'
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(subscribeToRouteChange(UserDetailsSceneComponent));

export { UserDetailsScene };
